import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { logoutUser } from '../../slices/auth/login/thunk';
import { APIClient } from '../../helpers/api_helper';
import { getUserAvatarUrl } from '../../helpers/imageUtils';

const ProfileDropdown = () => {
    const dispatch: any = useDispatch();
    const navigate = useNavigate();

    const [userName, setUserName] = useState<string | null>(null);
    const [userImage, setUserImage] = useState<string | null>(null);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [userId, setUserId] = useState<number | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Initial load from session storage
    useEffect(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (authUser) {
            try {
                const obj = JSON.parse(authUser);
                // Use real user data from our authentication system
                if (obj.user) {
                    const userIdNum = obj.user.id;
                    setUserId(userIdNum);
                    setUserName(obj.user.full_name || obj.user.username || "Admin");
                    
                    // Use the utility function to get a valid image URL
                    const avatarUrl = getUserAvatarUrl(obj.user);
                    setUserImage(avatarUrl);
                    
                    // Set role based on level_name or fallback to permissions
                    if (obj.user.level_name) {
                        setUserRole(obj.user.level_name);
                    } else if (obj.user.role) {
                        setUserRole(obj.user.role);
                    } else if (obj.user.permissions && obj.user.permissions.includes('admin')) {
                        setUserRole("Admin");
                    } else if (obj.user.permissions && obj.user.permissions.includes('editor')) {
                        setUserRole("Editor");
                    } else if (obj.user.permissions && obj.user.permissions.includes('viewer')) {
                        setUserRole("Viewer");
                    } else if (obj.user.username === 'admin' || obj.user.id === 1) {
                        setUserRole("Founder");
                    }
                    
                    setIsInitialized(true);
                }
            } catch (error) {
                console.error('Error parsing auth user:', error);
            }
        }
    }, []);

    // Fetch latest user data from backend
    useEffect(() => {
        if (userId && isInitialized) {
            const api = new APIClient();
            api.get(`/users/${userId}`)
                .then(res => {
                    if (res.data) {
                        setUserName(res.data.full_name || res.data.username || "Admin");
                        // Use the utility function to get a valid image URL
                        const avatarUrl = getUserAvatarUrl(res.data);
                        setUserImage(avatarUrl);
                        // Update role based on level_name or fallback to permissions
                        if (res.data.level_name) {
                            setUserRole(res.data.level_name);
                        } else if (res.data.role) {
                            setUserRole(res.data.role);
                        } else if (res.data.permissions && res.data.permissions.includes('admin')) {
                            setUserRole("Admin");
                        } else if (res.data.permissions && res.data.permissions.includes('editor')) {
                            setUserRole("Editor");
                        } else if (res.data.permissions && res.data.permissions.includes('viewer')) {
                            setUserRole("Viewer");
                        }
                    }
                })
                .catch(err => console.error('Error fetching user data:', err));
        }
    }, [userId, isInitialized]);

    //Dropdown Toggle
    const [isProfileDropdown, setIsProfileDropdown] = useState(false);
    const toggleProfileDropdown = () => {
        setIsProfileDropdown(!isProfileDropdown);
    };

    const handleLogout = () => {
        dispatch(logoutUser());
        navigate('/login');
    };
    return (
        <React.Fragment>
            <Dropdown isOpen={isProfileDropdown} toggle={toggleProfileDropdown} className="ms-sm-3 header-item topbar-user">
                <DropdownToggle tag="button" type="button" className="btn">
                    <span className="d-flex align-items-center">
                        {userImage ? (
                            <img className="rounded-circle header-profile-user" src={userImage}
                                alt="Header Avatar" 
                                onError={(e) => {
                                    console.error('Failed to load user avatar:', userImage);
                                    // Update state to use default avatar
                                    setUserImage(getUserAvatarUrl(null));
                                }}
                                style={{ width: '37px', height: '37px', objectFit: 'cover' }}
                            />
                        ) : (
                            <div className="rounded-circle header-profile-user bg-soft-primary text-primary d-flex align-items-center justify-content-center" 
                                 style={{ width: '37px', height: '37px' }}>
                                <i className="ri-user-3-line"></i>
                            </div>
                        )}
                        <span className="text-start ms-xl-2">
                            <span className="d-none d-xl-inline-block ms-1 fw-medium user-name-text"> {userName || "Loading..."}</span>
                            <span className="d-none d-xl-block ms-1 fs-12 text-muted user-name-sub-text">{userRole || "..."}</span>
                        </span>
                    </span>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                    <h6 className="dropdown-header">Welcome {userName}!</h6>
                    <DropdownItem className='p-0'>
                        <Link to={userId ? `/users/${userId}` : "/profile"} className="dropdown-item">
                            <i className="mdi mdi-account-circle text-muted fs-16 align-middle me-1"></i>
                            <span className="align-middle">Profile</span>
                        </Link>
                    </DropdownItem>
                    <DropdownItem className='p-0'>
                        <Link to="/messages" className="dropdown-item">
                            <i className="mdi mdi-message-text-outline text-muted fs-16 align-middle me-1"></i> <span
                                className="align-middle">Messages</span>
                        </Link>
                    </DropdownItem>
                    <DropdownItem className='p-0'>
                        <Link to={userId ? `/users/${userId}` : "#"} className="dropdown-item">
                            <i className="mdi mdi-calendar-check-outline text-muted fs-16 align-middle me-1"></i> <span
                                className="align-middle">Taskboard</span>
                        </Link>
                    </DropdownItem>
                    <DropdownItem className='p-0'>
                        <Link to="/wiki" className="dropdown-item">
                            <i
                                className="mdi mdi-lifebuoy text-muted fs-16 align-middle me-1"></i> <span
                                    className="align-middle">Help</span>
                        </Link>
                    </DropdownItem>
                    <div className="dropdown-divider"></div>
                    <DropdownItem onClick={handleLogout}>
                        <i
                            className="mdi mdi-logout text-muted fs-16 align-middle me-1"></i> <span
                                className="align-middle" data-key="t-logout">Logout</span>
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>
        </React.Fragment>
    );
};

export default ProfileDropdown;